import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ReclassificationStatement } from '@modeso/types__aumico-lib-annualreport-be';
import { I18NextService } from 'angular-i18next';

/**
 * Nested overlay for creating a GROUP on-the-fly as a reclassification target — the group branch of
 * the "+" button (the account branch is `aumico-new-account-nested-modal`).
 *
 * The user supplies only what a group needs: a name, and the FLI it belongs to. The parent then does
 * what picking an existing group from the target dropdown already does — create the group and
 * materialize a `MAN` reclassification account inside it that receives the amount.
 */
@Component({
    selector: 'aumico-new-group-nested-modal',
    templateUrl: './new-group-nested-modal.component.html',
    styleUrls: ['./new-group-nested-modal.component.scss'],
})
export class NewGroupNestedModalComponent implements OnInit {
    /** Statement the group is created in; decides which FLIs are offered. */
    @Input() statement!: ReclassificationStatement;

    /** FLIs of the current statement — the group must live in one of them. */
    @Input() fliOptions: Array<{ id: string; label: string }> = [];

    /**
     * Names of the groups that already exist, per FLI id. A name that is already taken in the chosen
     * FLI is not an error — the group id is derived from container + name, so the reclassification
     * simply lands in that existing group — but the user is told, because "create" would otherwise
     * look like it silently did nothing new.
     */
    @Input() existingGroupNamesByFli: Record<string, string[]> = {};

    @Output() created = new EventEmitter<{
        groupName: string;
        fliId: string;
        statement: ReclassificationStatement;
    }>();
    @Output() cancelled = new EventEmitter<void>();

    groupName = '';
    fliId = '';
    submitAttempted = false;

    // Localized texts
    title = '';
    groupNameLabel = '';
    groupNamePlaceholder = '';
    fliLabel = '';
    fliPlaceholder = '';
    cancelLabel = '';
    saveLabel = '';
    errGroupNameRequired = '';
    errFliRequired = '';
    hintGroupExists = '';

    constructor(private readonly i18n: I18NextService) {}

    ngOnInit(): void {
        const t = (k: string): string => this.i18n.t(k) as string;
        this.title = t('aumico_grouping_masking_reclass_new_group_modal_title');
        this.groupNameLabel = t('aumico_grouping_masking_reclass_new_group_name_label');
        this.groupNamePlaceholder = t('aumico_grouping_masking_reclass_new_group_name_placeholder');
        this.fliLabel = t('aumico_grouping_masking_reclass_new_group_fli_label');
        this.fliPlaceholder = t('aumico_grouping_masking_reclass_new_group_fli_placeholder');
        this.cancelLabel = t('aumico_grouping_masking_modal_cancel');
        this.saveLabel = t('aumico_grouping_masking_reclass_new_group_save');
        this.errGroupNameRequired = t('aumico_grouping_masking_reclass_new_group_name_required');
        this.errFliRequired = t('aumico_grouping_masking_reclass_new_group_fli_required');
        this.hintGroupExists = t('aumico_grouping_masking_reclass_new_group_name_exists');
    }

    get isGroupNameEmpty(): boolean {
        return this.groupName.trim().length === 0;
    }

    get isFliEmpty(): boolean {
        return this.fliId.trim().length === 0;
    }

    /** Whether the chosen FLI already holds a group under this name (a hint, not an error). */
    get groupAlreadyExists(): boolean {
        const name = this.groupName.trim();
        if (name.length === 0 || this.isFliEmpty) return false;
        const taken = this.existingGroupNamesByFli[this.fliId] ?? [];
        return taken.some((existing) => existing.trim().toLowerCase() === name.toLowerCase());
    }

    get hasErrors(): boolean {
        return this.isGroupNameEmpty || this.isFliEmpty;
    }

    onSave(): void {
        this.submitAttempted = true;
        if (this.hasErrors) return;
        this.created.emit({
            groupName: this.groupName.trim(),
            fliId: this.fliId,
            statement: this.statement,
        });
    }

    onCancel(): void {
        this.cancelled.emit();
    }
}
